import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { scheduleNextBoundary, venueCurrentTime, findScheduleById } from 'utils/scheduleEvaluator';
import { scheduleTick } from 'actions/browse';
import useResumeSignal from './useResumeSignal';

// setTimeout stores its delay in a 32-bit signed int; anything larger fires
// immediately, which combined with the scheduleTick dep below would loop forever.
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

// Backstop against a zero-delay re-dispatch loop: if a boundary ever computes at
// or before currentTime without the active state actually flipping (an
// isScheduleActive vs getNextActiveTime disagreement is possible around
// cross-midnight windows), a 0ms timeout would dispatch scheduleTick, re-run this
// effect, recompute the same boundary, and spin — also triggering orderMiddleware's
// cart sweep each pass. Flooring the delay guarantees the loop can't run hot.
// Real boundaries are typically far in the future, so this never affects them.
const MIN_TIMEOUT_MS = 1000;

// Finds the earliest upcoming schedule boundary across all menus/services, sets
// one setTimeout, then dispatches SCHEDULE_TICK so selectors recompute active state.
// Subscribes to scheduleTick in the store so any external dispatch (e.g. server-time
// offset correction) also causes the timeout to be recalculated.
const useScheduleTimer = ({ menus, services, schedules, venueTimezone }) => {
  const dispatch = useDispatch();
  const scheduleTickValue = useSelector(state => state.getIn(['browse', 'scheduleTick']));

  useEffect(() => {
    if (!schedules || schedules.size === 0) return undefined;
    if ((!menus || menus.size === 0) && (!services || services.size === 0)) return undefined;

    const currentTime = venueCurrentTime(venueTimezone);
    const boundaries = [];

    const pushBoundaryFor = scheduleId => {
      if (scheduleId === null || typeof scheduleId === 'undefined') return;
      const schedule = findScheduleById(schedules, scheduleId);
      const at = scheduleNextBoundary(schedule, currentTime);
      if (at) boundaries.push(at);
    };

    menus?.forEach(menu => {
      pushBoundaryFor(menu.get('scheduleId'));

      menu.get('sections')?.forEach(section => {
        const ids = section.get('schedules');
        if (ids && ids.size > 0) ids.forEach(pushBoundaryFor);
      });
    });

    services?.forEach(service => pushBoundaryFor(service.get('scheduleId')));

    if (boundaries.length === 0) return undefined;

    const earliest = Math.min(...boundaries);
    const delayMs = Math.min(Math.max(earliest - currentTime.getTime(), MIN_TIMEOUT_MS), MAX_TIMEOUT_MS);

    const timeoutId = setTimeout(() => {
      dispatch(scheduleTick());
    }, delayMs);

    return () => clearTimeout(timeoutId);
  }, [menus, services, schedules, venueTimezone, dispatch, scheduleTickValue]);

  // Resync after tab backgrounding or device sleep.
  useResumeSignal(() => dispatch(scheduleTick()));
};

export default useScheduleTimer;
